import type { DefenseStrategy } from '../types';

// DRAFT for Glenn to review: the labels come from the sheet he sent, and the
// strategy each one serves is our first guess. A category with no strategy
// still shows in the directory but is never suggested from a plan.
export const VENDOR_CATEGORIES: { label: string; strategies: DefenseStrategy[] }[] = [
  { label: 'Tree Service', strategies: ['tree-thinning', 'limb-trees', 'remove-fuel'] },
  {
    label: 'Vegetation Management',
    strategies: ['brush-clearing', 'fire-break', 'remove-fuel', 'grazing'],
  },
  { label: 'Landscaping', strategies: ['brush-clearing', 'fire-break'] },
  { label: 'Hardscaping', strategies: ['fire-break', 'metal-fence'] },
  {
    label: 'Home Hardening Products and Installation',
    strategies: [
      'house-hardening',
      'defense-mesh',
      'upgrade-roofing',
      'upgrade-siding',
      'upgrade-vents',
      'upgrade-windows',
    ],
  },
  { label: 'Treated Lumber', strategies: ['upgrade-siding', 'metal-fence'] },
  { label: 'Fire Retardants/Inhibitors and Application', strategies: ['fireproof-paint'] },
  { label: 'Sprays and Foams', strategies: ['fireproof-paint'] },
  { label: 'Sprinklers and Sprinkler Systems', strategies: ['sprinklers'] },
  { label: 'Sprinkler Towers', strategies: ['sprinkler-towers'] },
  { label: 'Fire Detection Systems', strategies: ['fire-sensor'] },
  { label: 'Autonomous Fire Detection and Suppression', strategies: ['fire-sensor', 'sprinklers'] },
  { label: 'Wildfire Risk Assessors + Product/Service Solutions', strategies: [] },
  { label: 'Environmental Planning and Monitoring', strategies: [] },
  { label: 'Geospatial Mapping (GIS)', strategies: [] },
  { label: 'Emergency Preparedness Planning and Supplies', strategies: [] },
];

/** Case and whitespace insensitive key for comparing category labels. */
export function normalizeCategory(label: string): string {
  return label.trim().replace(/\s+/g, ' ').toLowerCase();
}

const BY_KEY = new Map(VENDOR_CATEGORIES.map((c) => [normalizeCategory(c.label), c]));

export function isKnownCategory(label: string): boolean {
  return BY_KEY.has(normalizeCategory(label));
}

/** The known label's own spelling when the text matches one, else the text
 *  trimmed. */
export function canonicalCategory(label: string): string {
  return BY_KEY.get(normalizeCategory(label))?.label ?? label.trim();
}

export function strategiesForCategory(label: string): DefenseStrategy[] {
  return BY_KEY.get(normalizeCategory(label))?.strategies ?? [];
}

// Service-area ids per county, for sheets that say "San Bernardino" instead
// of naming an area. Only counties with a service area are listed (ids are in
// scripts/seed-service-areas.ts). The misspelling is in Glenn's own sheet.
export const COUNTY_AREAS: Record<string, string[]> = {
  'san bernardino': ['big-bear-lake'],
  'san bernadino': ['big-bear-lake'],
};

export const NATIONAL_WORDS = [
  'national',
  'nationwide',
  'usa',
  'us',
  'united states',
  'all',
  'everywhere',
];

/** Service-area ids for a sheet cell, or null when no part of it matched. A
 *  cell can hold several values split by line breaks, commas or semicolons.
 *  A national word wins: '*' already covers every area, so it returns alone. */
export function areaIdsForServiceArea(text: string): string[] | null {
  const ids: string[] = [];
  let national = false;
  for (const part of text.split(/[\r\n,;]+/)) {
    const value = part.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
    if (!value) continue;
    if (NATIONAL_WORDS.includes(value)) {
      national = true;
      continue;
    }
    const county = value.replace(/ county$/, '');
    for (const id of COUNTY_AREAS[county] ?? []) if (!ids.includes(id)) ids.push(id);
  }
  if (national) return ['*'];
  return ids.length > 0 ? ids : null;
}
